import React, { useState, useEffect, useRef } from "react";
import { Loader2, CheckCircle, AlertCircle, X } from "lucide-react";
import { getSyncStatus } from "../services/tenantService";
import socketService from "../services/socketService";

export function SyncStatusBar() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const autoDismissTimerRef = useRef(null);

  // Fetch sync status from API
  const fetchSyncStatus = async () => {
    try {
      const response = await getSyncStatus();
      if (response?.success && response?.data) {
        setSyncStatus(response.data);

        // Auto-dismiss success messages after 5 seconds
        if (response.data.jobs && !response.data.jobs.isSyncing && !response.data.jobs.lastError) {
          if (autoDismissTimerRef.current) {
            clearTimeout(autoDismissTimerRef.current);
          }
          autoDismissTimerRef.current = setTimeout(() => {
            setDismissed(true);
          }, 5000);
        }
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  // Set up Socket.IO listeners for real-time updates
  useEffect(() => {
    // Initial fetch
    fetchSyncStatus();

    // Polling fallback every 30 seconds (in case Socket.IO misses an event)
    const pollInterval = setInterval(fetchSyncStatus, 30000);

    // Socket.IO event listeners
    const handleSyncStarted = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: true,
          lastError: null
        }
      }));
      setDismissed(false);
    };

    const handleSyncCompleted = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: false,
          lastSyncTime: new Date().toISOString(),
          lastSyncResult: data.result,
          lastError: null
        }
      }));
      setDismissed(false);

      // Auto-dismiss after 5 seconds
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
      }
      autoDismissTimerRef.current = setTimeout(() => {
        setDismissed(true);
      }, 5000);
    };

    const handleSyncFailed = (data) => {
      setSyncStatus(prev => ({
        ...prev,
        jobs: {
          ...prev?.jobs,
          isSyncing: false,
          lastError: data.error,
          lastSyncTime: new Date().toISOString()
        }
      }));
      setDismissed(false);
    };

    // Connect socket if not already connected
    if (!socketService.isConnected()) {
      socketService.connect();
    }

    socketService.on('sync:jobs_started', handleSyncStarted);
    socketService.on('sync:jobs_completed', handleSyncCompleted);
    socketService.on('sync:jobs_failed', handleSyncFailed);

    return () => {
      clearInterval(pollInterval);
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
      }
      socketService.off('sync:jobs_started', handleSyncStarted);
      socketService.off('sync:jobs_completed', handleSyncCompleted);
      socketService.off('sync:jobs_failed', handleSyncFailed);
    };
  }, []);

  // Don't show if dismissed or no sync status
  if (dismissed || !syncStatus?.jobs) {
    return null;
  }

  const jobSync = syncStatus.jobs;

  // Determine what to display
  const isSyncing = jobSync.isSyncing;
  const hasError = jobSync.lastError;
  const lastSyncTime = jobSync.lastSyncTime ? new Date(jobSync.lastSyncTime) : null;
  const newJobs = jobSync.lastSyncResult?.new || 0;
  const updatedJobs = jobSync.lastSyncResult?.updated || 0;

  // Don't show if never synced and not currently syncing
  if (!isSyncing && !lastSyncTime && !hasError) {
    return null;
  }

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div
      className={`rounded-lg p-3 flex items-center justify-between text-sm ${
        hasError
          ? 'bg-red-50 border border-red-200 text-red-800'
          : isSyncing
          ? 'bg-blue-50 border border-blue-200 text-blue-800'
          : 'bg-green-50 border border-green-200 text-green-800'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        {hasError ? (
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
        ) : isSyncing ? (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
        ) : (
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
        )}

        {/* Message */}
        <div>
          {hasError ? (
            <>
              <span className="font-medium">Sync failed: </span>
              <span>{jobSync.lastError}</span>
            </>
          ) : isSyncing ? (
            <span className="font-medium">Fetching new jobs from Upwork...</span>
          ) : (
            <>
              <span className="font-medium">Jobs synced successfully</span>
              {(newJobs > 0 || updatedJobs > 0) && (
                <span className="ml-1">
                  ({newJobs > 0 && `${newJobs} new`}
                  {newJobs > 0 && updatedJobs > 0 && ', '}
                  {updatedJobs > 0 && `${updatedJobs} updated`})
                </span>
              )}
              {lastSyncTime && (
                <span className="ml-1 text-xs opacity-75">
                  • {getTimeAgo(lastSyncTime)}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Dismiss button (only for success and error states, not while syncing) */}
      {!isSyncing && (
        <button
          onClick={() => setDismissed(true)}
          className={`p-1 rounded hover:bg-white/50 transition-colors ${
            hasError ? 'text-red-600' : 'text-green-600'
          }`}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
